import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { X, Download, Upload } from "lucide-react";
import FormDesign from "./FormDesign";
import { useAuth } from "../../../../firebase/AuthProvider";
import {
  createUser,
  getExistUser,
  uploadUsersFromCsv,
} from "../../../../services/userServices/TeamService";
import Loader from "../../loader/Loader";
import Role from "../../../../interfaces/identity/Role";
import Region from "../../../../interfaces/identity/Region";
import { getAllRegions } from "../../../../services/userServices/UserInfoService";
import {
  getReportTo,
  getRoles,
} from "../../../../services/userServices/UserRolesService";
import {
  GetReportBody,
  reportTo,
  teams,
} from "../../../../interfaces/team/RegionHierarchy";
import { getAllTeamByRegions } from "../../../../services/calendarService/EventService";
import { ROLE_IDS, ROLE_PRIORITY, ROLES } from "../../../../Data/SD";
import Waiver from "../../../../interfaces/participant/Waiver";
import { getWaivers } from "../../../../services/participantServices/ParticipantService";
import { AllTeams } from "../../../../interfaces/identity/calendar/Events";

 export const isInvalidWaiverFamilySelection = (waiverFamilyIds: number[], waivers: Waiver[]): boolean => {
  if (waiverFamilyIds.length < 2) return false;
  
  const selectedWaivers = waivers.filter(w => waiverFamilyIds.includes(w.id));
  const waiverCodes = selectedWaivers.map(w => w.waiverCode?.trim().toUpperCase()).filter(Boolean);
  
  const hasOAAS = waiverCodes.includes("OAAS");
  const hasOCDD = waiverCodes.includes("OCDD");
  
  return hasOAAS && hasOCDD;
};

export interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  displayName: string;
  phoneNumber: string;
  employeeId: string;
  returnSecureToken: boolean;
  organizationId: number;
  roleIds: number[];
  regionIds: number[];
  reportTo: number[];
  Createdby: string;
  ManagerId: string;
  TeamIds: number[];
  file: File | string | null;
  timeZone: string;
  waiverId: number | null;
  waiverCode: string;
  waiverFamilyIds: number[];
}

export type FormErrors = Partial<
  Record<keyof FormData | "roleIds" | "regionIds", string>
>;

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialTeam?: AllTeams | null;
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialTeam,
}) => {
  const { currentUser } = useAuth();
  const userId = (currentUser as any)?.uid;

  const [activeTab, setActiveTab] = useState<"single" | "bulk">("single");
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [reportTo, setReportTo] = useState<reportTo[]>([]);
  const [waivers, setWaivers] = useState<Waiver[]>([]);
  const [teams, setTeams] = useState<teams[]>([]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const initialFormData: FormData = {
    firstName: "",
    lastName: "",
    email: "",
    displayName: "",
    phoneNumber: "",
    employeeId: "",
    returnSecureToken: true,
    Createdby: userId,
    ManagerId: "",
    organizationId: 0,
    roleIds: [],
    regionIds: [],
    reportTo: [],
    TeamIds: [],
    file: null,
    timeZone: "",
    waiverId: null,
    waiverCode: "",
    waiverFamilyIds: [],
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);

  const validateForm = useCallback(async (): Promise<boolean> => {
    const newErrors: FormErrors = {};
    const nameRegex = /^[A-Za-z''-]+(?: [A-Za-z''-]+)*$/;

    const validateNameField = (
      value: string,
      field: "firstName" | "lastName"
    ) => {
      const label = field === "firstName" ? "First Name" : "Last Name";

      if (!value?.trim()) {
        newErrors[field] = `${label} is required`;
        return;
      }

      const trimmed = value.trim();

      if (trimmed.length > 50) {
        newErrors[field] = `${label} cannot exceed 50 characters`;
        return;
      }

      if (!nameRegex.test(trimmed)) {
        newErrors[field] = `${label} contains invalid characters`;
        return;
      }

      if (/ {2,}/.test(trimmed)) {
        newErrors[field] = `${label} cannot contain multiple spaces`;
        return;
      }
    };

    validateNameField(formData.firstName, "firstName");
    validateNameField(formData.lastName, "lastName");

    if (!formData.displayName?.trim())
      newErrors.displayName = "Display Name is required";

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    } else {
      const authToken = await currentUser?.getIdToken();
      if (authToken) {
        const isEmailTaken = await getExistUser(formData.email, authToken);
        if (isEmailTaken) {
          newErrors.email = "Email already exists";
        }
      }
    }

    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 10 digits";
    }

    if (!formData.employeeId?.trim())
      newErrors.employeeId = "Employee Id is required";

    if (!formData.roleIds || formData.roleIds.length === 0) {
      newErrors.roleIds = "At least one role must be selected";
    }

    if (!formData.regionIds || formData.regionIds.length === 0) {
      newErrors.regionIds = "At least one region must be selected";
    }

     if (
      formData.waiverFamilyIds.length > 0 &&
      isInvalidWaiverFamilySelection(formData.waiverFamilyIds, waivers)
    ) {
      newErrors.waiverFamilyIds = "OAAS and OCDD cannot be selected together";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, currentUser, waivers]);

  const handleChange = useCallback(
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
        | {
          target: { name: string; value: any; type: string };
        }
    ) => {
      const { name, value, type } = e.target;

      if (name === "roleIds" && type === "checkbox-group") {
        const selectedRoleIds: number[] = value as number[];
        setFormData((prev) => ({
          ...prev,
          [name]: selectedRoleIds,
        }));
        return;
      }

      if (name === "waiverFamilyIds" && type === "checkbox-group") {
        const selectedWaiverFamilyIds: number[] = value as number[];
        setFormData((prev) => ({
          ...prev,
          [name]: selectedWaiverFamilyIds,
        }));
        return;
      }

      if (name === "TeamIds" && type === "checkbox-group") {
        setFormData((prev) => ({
          ...prev,
          [name]: value as number[],
        }));
        return;
      } else if (type === "file") {
        const inputElement = e.target as HTMLInputElement;
        const file = inputElement.files?.[0] || null;
        setFormData((prev) => ({
          ...prev,
          [name]: file,
        }));
      } else if (type === "checkbox") {
        const inputElement = e.target as HTMLInputElement;
        setFormData((prev) => ({
          ...prev,
          [name]: inputElement.checked,
        }));
      } else if (name === "waiverId") {
        // Handle waiverId specifically to also update waiverCode
        const selectedId = value ? Number(value) : null;
        const selectedWaiver = waivers.find((w) => w.id === selectedId);
        
        setFormData((prev) => ({
          ...prev,
          waiverId: selectedId,
          waiverCode: selectedWaiver?.waiverCode || "",
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    },
    [roles, waivers]
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!(await validateForm())) return;
    if (!currentUser) return;
    const authToken = await currentUser.getIdToken();
    if (!authToken) {
      toast.error("Authentication error. Please log in again.");
      return;
    }
    setLoading(true);

    try {
      const data = new FormData();
      data.append("firstName", formData.firstName.trim());
      data.append("lastName", formData.lastName.trim());
      data.append("email", formData.email);
      data.append("displayName", formData.displayName.trim());
      if (formData.waiverId) {
        data.append("WaiverId", String(formData.waiverId));
      }
      if (formData.waiverCode) {
        data.append("WaiverCode", formData.waiverCode);
      }
      data.append("phoneNumber", formData.phoneNumber);
      data.append("employeeId", formData.employeeId);
      data.append("returnSecureToken", String(formData.returnSecureToken));
      data.append("ManagerId", String(formData.ManagerId));
      data.append("Createdby", formData.Createdby);
      data.append("timeZone", formData.timeZone);
      formData.TeamIds.forEach((id) => data.append("TeamIds", String(id)));
      formData.roleIds.forEach((id) => data.append("roleIds", String(id)));
      formData.regionIds.forEach((id) => data.append("regionIds", String(id)));
      formData.waiverFamilyIds.forEach((id) => data.append("WaiverFamilyIds", String(id)));

      if (formData.file instanceof File) {
        data.append("file", formData.file);
      }

      const userRes = await createUser(data, authToken);
      if (userRes.success) {
        toast.success("User created successfully!");
        setFormData(initialFormData);
        setErrors({});
        onSuccess?.();
        onClose();
      } else {
        toast.error(userRes.message);
      }
    } catch (error: any) {
      console.error("Error creating user:", error);
      toast.error(
        error.message ||
        `Error creating user: ${error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      if (!currentUser) return;
      const authToken = await currentUser.getIdToken();
      if (!authToken) {
        console.warn("No auth token available for fetching data.");
        toast.warn("Authentication details missing.");
        return;
      }
      setLoading(true);
      try {
        const [rolesData, regionsData, waiverRes] = await Promise.all([
          getRoles(authToken),
          getAllRegions(authToken),
          getWaivers(authToken),
        ]);

        if (rolesData) {
          setRoles(rolesData);
        } else {
          toast.error("Failed to load roles.");
        }

        if (regionsData) {
          setRegions(regionsData.data);
        } else {
          toast.error("Failed to load regions.");
        }
        if (waiverRes?.success && waiverRes.data?.length > 0) {
          setWaivers(waiverRes.data);
        } else if (waiverRes?.success && waiverRes.data?.length === 0) {
          setWaivers([]);
          toast.info("No waiver families available.");
        } else {
          setWaivers([]);
          toast.error("Failed to load waiver families.");
        }
      } catch (error) {
        console.error("Error fetching roles, regions, or waiver families:", error);
        toast.error("Error fetching initial data.");
        setWaivers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, isOpen]);

  const fetchReportToList = async () => {
    if (!currentUser) return;
    const authToken = await currentUser.getIdToken();
    if (
      !authToken ||
      !formData.regionIds.length ||
      !formData.roleIds.length ||
      (!formData.TeamIds.length &&
        !formData.roleIds.includes(ROLE_IDS.PROGRAM_MANAGER))
    )
      return;

    const requestBody: GetReportBody = {
      regionIds: formData.regionIds,
      roleIds: formData.roleIds,
      teamIds: formData.TeamIds,
    };

    try {
      setReportTo([]);
      console.log("requestBody========================>", requestBody);
      const reportToList = await getReportTo(authToken, requestBody);
      console.log("reportToList========================>", reportToList);
      if (reportToList?.success) {
        setReportTo(reportToList.data);

        if (!reportToList.data.some((m) => m.id == formData.ManagerId)) {
          handleChange({
            target: { name: "ManagerId", value: "", type: "string" },
          });
        }
      } else {
        toast.error(reportToList?.message);
      }
    } catch (error) {
      toast.error("Failed to fetch reporting users.");
      console.error(error);
    }
  };

  const fetchTeamsList = async () => {
    if (!currentUser) return;
    const authToken = await currentUser.getIdToken();
    if (!authToken || formData.regionIds.length == 0) return;
    try {
      const teamsList = await getAllTeamByRegions(
        authToken,
        formData.regionIds
      );
      if (teamsList) setTeams(teamsList);
    } catch (error) {
      toast.error("Failed to fetch teams.");
      console.error(error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchReportToList();
    }
  }, [formData.roleIds, formData.regionIds, formData.TeamIds, isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchTeamsList();
    }
  }, [formData.regionIds, isOpen]);

  useEffect(() => {
    if (isOpen && initialTeam) {
      setFormData((prev) => ({
        ...prev,
        regionIds: [initialTeam.regionId],
        TeamIds: [initialTeam.id],
      }));
    }
  }, [isOpen, initialTeam]);

  const downloadCsvTemplate = () => {
    // Define CSV headers based on the form fields
    const headers = [
      'firstName',
      'lastName', 
      'email',
      'displayName',
      'phoneNumber',
      'employeeId',
      'roleIds',
      'regionIds',
      'TeamIds',
      'ManagerId',
      'timeZone',
      'WaiverId',
      'WaiverCode'
    ];

    // Create a sample row with example data
    const sampleRow = [
      'John',
      'Doe',
      'john.doe@example.com',
      'John Doe',
      '1234567890',
      'EMP001',
      '1', // Example role ID
      '1', // Example region ID
      '1', // Example team ID
      '', // Manager ID (optional)
      'America/New_York', // Example timezone
      '', // Waiver ID (optional)
      ''  // Waiver Code (optional)
    ];

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      sampleRow.join(',')
    ].join('\n');

    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'user_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV template downloaded successfully!');
  };

  if (!isOpen) return null;

  return (
    <>
      {loading && <Loader />}
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto no-scrollbar bg-black bg-opacity-50 "
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-[900px] overflow-y-auto rounded-2xl bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white   rounded-t-2xl z-10">
            <div className="flex items-center justify-between border-b border-[#E5E7EB] py-4 px-6 mb-4">
              <h2 className="m-0 block text-[#0A0A0A] font-poppins text-xl font-bold leading-tight">
                Add Member
              </h2>

              <button
                onClick={onClose}
                className="flex items-center text-[#0A0A0A] hover:text-gray-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex px-6 gap-2">
              <button
                onClick={() => setActiveTab("single")}
                className={`px-4 py-2 text-sm font-medium font-poppins rounded-[0.62rem] transition-colors ${activeTab === "single"
                    ? "bg-[#7E57C2] text-white"
                    : "bg-[#F3F4F6] text-[#747474] hover:bg-gray-200"
                  }`}
              >
                Add One Member
              </button>
              <button
                onClick={() => setActiveTab("bulk")}
                className={`px-4 py-2 text-sm font-medium font-poppins rounded-[0.62rem] transition-colors ${activeTab === "bulk"
                    ? "bg-[#7E57C2] text-white"
                    : "bg-[#F3F4F6] text-[#747474] hover:bg-gray-200"
                  }`}
              >
                Upload Multiple (CSV)
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {activeTab === "single" ? (
              <FormDesign
                handleSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                handleCancel={handleCancel}
                roles={roles}
                regions={regions}
                reportTo={reportTo}
                teams={teams}
                waivers={waivers}
                errors={errors}
                hideRegionTeamSelection={!!initialTeam}
              />
            ) : (
              <div className="space-y-6">
                {/* Instructions Section */}
                <div className="bg-[#F3F4F6] border border-[#9CA3AF] rounded-[0.625rem] p-2">
                  <h3 className="text-[#0A0A0A] pl-1 font-poppins text-base font-semibold mb-1">
                    Instructions:
                  </h3>
                  <ol className="space-y-0.5 text-[#747474] font-poppins text-sm font-normal pl-1 leading-tight">
                    <li>Step 1 - Download the CSV template</li>
                    <li>Step 2 - Fill in member information</li>
                    <li>Step 3 - Upload the completed CSV file</li>
                  </ol>
                </div>

                {/* Download CSV Template Button */}
                <button
                  type="button"
                  onClick={() => {
                    downloadCsvTemplate();
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-white border border-[#7E57C2] text-[#7E57C2] font-poppins font-medium py-3 px-4 rounded-[0.625rem] hover:bg-[#F3E8FF] transition-colors"
                >
                  <Download size={20} />
                  <span>Download CSV Template</span>
                </button>

                {/* Upload Completed CSV Section */}
                <div>
                  <h3 className="text-[#0A0A0A] text-base mb-3">
                    Upload Completed CSV
                  </h3>
                  <div
                    className={`border-2 border-dashed rounded-[0.625rem] p-8 text-center transition-colors ${isDragging
                        ? "border-[#7E57C2] bg-[#F3E8FF]"
                        : "border-[#C9C9C9] bg-[#F9FAFB]"
                      }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const file = e.dataTransfer.files[0];
                      if (file && file.type === "text/csv") {
                        setCsvFile(file);
                      } else {
                        toast.error("Please upload a CSV file only");
                      }
                    }}
                  >
                    <input
                      type="file"
                      id="csv-upload"
                      accept=".csv"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setCsvFile(file);
                        }
                      }}
                    />
                    {csvFile ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center">
                          <Upload size={32} className="text-[#7E57C2]" />
                        </div>
                        <p className="text-[#0A0A0A] font-poppins text-sm font-medium">
                          {csvFile.name}
                        </p>
                        <button
                          type="button"
                          onClick={() => setCsvFile(null)}
                          className="text-[#7E57C2] font-poppins text-sm hover:underline"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <label htmlFor="csv-upload" className="cursor-pointer">
                        <div className="flex flex-col items-center gap-0.5">
                          <Upload size={32} className="text-[#747474]" />
                          <p className="text-[#0A0A0A] font-poppins text-sm font-medium">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-[#747474] font-poppins text-xs">
                            CSV files only
                          </p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Bottom Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-2 bg-[#F3F4F6] text-[#747474] font-poppins text-sm font-medium rounded-[0.625rem] hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!csvFile) {
                        toast.error("Please upload a CSV file");
                        return;
                      }
                      
                      try {
                        if (!currentUser) {
                          toast.error("User not authenticated");
                          return;
                        }
                        setLoading(true);
                        const authToken = await currentUser.getIdToken();
                        const result = await uploadUsersFromCsv(csvFile, authToken);
                        
                        if (result.success) {
                          const successfulRecords = result.data?.successfulRecords || 0;
                          const failedRecords = result.data?.failedRecords || 0;
                          const errors = result.data?.errors || [];
                          
                          if (failedRecords > 0) {
                            // Show specific errors in toast
                            const errorMessages = errors.slice(0, 3).map((err: any) => 
                              `Row ${err.rowNumber}: ${err.errorMessage}`
                            ).join('\n');
                            
                            toast.error(
                              `${failedRecords} users failed to upload:\n${errorMessages}${errors.length > 3 ? '\n...' : ''}`,
                              { autoClose: 10000 }
                            );
                          }
                          
                          // Only show success message if there are successful records
                          if (successfulRecords > 0) {
                            toast.success(
                              `CSV upload completed! ${successfulRecords} users created successfully.${failedRecords > 0 ? ` ${failedRecords} failed.` : ''}`
                            );
                          }
                          
                          setCsvFile(null);
                          setFormData(initialFormData);
                          onSuccess?.();
                          onClose();
                        } else {
                          // Show specific error message from API response
                          const errorMessage = result.data?.errors?.[0]?.errorMessage || result.message || "CSV upload failed";
                          toast.error(errorMessage);
                        }
                      } catch (error) {
                        console.error("CSV Upload Error:", error);
                        toast.error("Failed to upload CSV. Please try again.");
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="px-6 py-2 bg-[#7E57C2] text-white font-poppins text-sm font-medium rounded-[0.625rem] hover:bg-[#6D4CAB] transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddUserModal;
